import { useMemo, useState } from 'react';
import { Check, Search } from 'lucide-react';
import { Label } from '@/components/ui/Label';
import { cn } from '@/lib/utils';
import { useItems, type Item } from '@/features/inventory/hooks';
import { isArchivedStatus } from '@/features/inventory/itemStatus';

interface ItemPickerProps {
  label?: string;
  /** Currently selected item id. */
  value: string | null;
  onChange: (item: Item | null) => void;
  placeholder?: string;
  autoFocus?: boolean;
  /** Restrict the candidate list (e.g. exclude the current item). */
  filter?: (item: Item) => boolean;
  /**
   * Archived items are omitted. The current value stays in the list so an
   * existing selection does not disappear. Pass true only for a picker that
   * is intentionally browsing the archive.
   */
  includeArchived?: boolean;
}

/**
 * Searchable single-select item picker shared by the stock-movement and
 * adjustment dialogs. Mirrors the inline picker in `AddToBomDialog` so the
 * select-an-item UX stays identical across the inventory module. Fetches
 * the full item master via `useItems` and filters client-side by SKU or
 * name. Archived items are not offered for new movements, purchases, or
 * production.
 */
export function ItemPicker({
  label = 'Item',
  value,
  onChange,
  placeholder = 'Search items by SKU or name…',
  autoFocus,
  filter,
  includeArchived = false,
}: ItemPickerProps) {
  const { data: items, isLoading } = useItems();
  const [query, setQuery] = useState('');

  const candidates = useMemo(() => {
    const base = (items ?? []).filter(
      (item) => includeArchived || !isArchivedStatus(item.status) || item.id === value,
    );
    return filter ? base.filter(filter) : base;
  }, [items, filter, includeArchived, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter(
      (it) => it.name.toLowerCase().includes(q) || it.sku.toLowerCase().includes(q),
    );
  }, [candidates, query]);

  return (
    <div className="space-y-1.5">
      {label && <Label>{label}</Label>}
      <div className="flex items-center gap-2 rounded-md border border-border px-3 py-2">
        <Search className="h-3.5 w-3.5 text-muted-foreground" />
        <input
          autoFocus={autoFocus}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
        />
      </div>
      <div className="max-h-52 overflow-y-auto rounded-md border border-border">
        {isLoading ? (
          <div className="px-3 py-6 text-center text-xs text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-muted-foreground">
            {candidates.length === 0 ? 'No items in the catalog yet.' : 'No matching items.'}
          </div>
        ) : (
          filtered.map((it) => {
            const isSelected = it.id === value;
            return (
              <button
                key={it.id}
                type="button"
                onClick={() => onChange(isSelected ? null : it)}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors hover:bg-secondary/60',
                  isSelected && 'bg-secondary',
                )}
              >
                <span
                  className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-border',
                    isSelected ? 'border-primary bg-primary text-primary-foreground' : 'opacity-40',
                  )}
                >
                  {isSelected && <Check className="h-3 w-3" />}
                </span>
                <span className="shrink-0 font-mono text-xs text-muted-foreground">{it.sku}</span>
                <span className="truncate">{it.name}</span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
